import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormField, Select, inputClass } from "@/components/ui";
import { CalendarIcon } from "@/components/icons";
import { useOccurrencePreview } from "@/hooks/use-occurrence-preview";
import { OccurrencePreview } from "./occurrence-preview";
import {
  WEEKDAY_ORDER,
  defaultStateFor,
  emitRRule,
  parseLocalDate,
  presetState,
  type RecurrenceBuilderState,
  type RecurrenceFrequency,
  type RecurrencePreset,
  type Weekday,
} from "./recurrence-rrule";
import { summarizeRecurrence } from "./recurrence-summary";

const RECURRENCE = "schedules.wizard.recurrence";
const ADVANCED = "schedules.wizard.advanced";

/** Mirrors the mockup's After-stepper seed when the radio is first selected. */
const DEFAULT_AFTER_COUNT = 12;
/** Engine cap on COUNT (occurrences 1..366). */
const MAX_AFTER_COUNT = 366;
const COPY_FEEDBACK_MS = 2000;

const PRESET_ORDER: readonly RecurrencePreset[] = [
  "daily",
  "weekly",
  "everyWeekday",
  "monthly",
  "quarterly",
  "yearly",
];

const MONTH_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

/** The wizard-level recurrence unit: builder state + its three companions. */
export interface RecurrenceValue {
  state: RecurrenceBuilderState;
  startDate: string;
  /** Mirrors `ends: { type: "on" }` — travels as the schedule's `end_date`. */
  endDate: string | null;
  horizonDays: number;
}

interface RecurrenceBuilderProps {
  value: RecurrenceValue;
  onChange: (value: RecurrenceValue) => void;
}

/**
 * Preset the current state expresses, or null when only "Custom…" can render
 * it faithfully. Presets always end "never", so any Ends choice is custom.
 */
function matchPreset(state: RecurrenceBuilderState, startDate: string): RecurrencePreset | null {
  if (startDate === "" || state.ends.type !== "never" || state.interval < 1) {
    return null;
  }
  const emitted = emitRRule(state);
  return (
    PRESET_ORDER.find((preset) => emitRRule(presetState(preset, startDate)) === emitted) ?? null
  );
}

function chipClass(selected: boolean): string {
  const base = "inline-flex h-9 items-center justify-center rounded-md border text-sm";
  return selected
    ? `${base} border-primary bg-primary font-semibold text-on-primary`
    : `${base} border-gray-300 bg-white text-on-surface hover:border-primary dark:border-gray-600 dark:bg-gray-700 dark:text-white`;
}

interface StepperProps {
  value: number;
  min: number;
  max?: number;
  disabled?: boolean;
  decreaseLabel: string;
  increaseLabel: string;
  onChange: (value: number) => void;
}

function Stepper({
  value,
  min,
  max,
  disabled = false,
  decreaseLabel,
  increaseLabel,
  onChange,
}: StepperProps) {
  const buttonClass =
    "h-9 w-8 text-base text-muted hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-600";
  return (
    <span
      className={`inline-flex items-center overflow-hidden rounded-md border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-700 ${disabled ? "opacity-50" : ""}`}
    >
      <button
        type="button"
        aria-label={decreaseLabel}
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        className={buttonClass}
      >
        −
      </button>
      <span className="w-11 border-x border-gray-200 py-1.5 text-center text-sm font-semibold text-on-surface dark:border-gray-600 dark:text-white">
        {value}
      </span>
      <button
        type="button"
        aria-label={increaseLabel}
        disabled={disabled || (max !== undefined && value >= max)}
        onClick={() => onChange(max === undefined ? value + 1 : Math.min(max, value + 1))}
        className={buttonClass}
      >
        +
      </button>
    </span>
  );
}

/**
 * Visual recurrence builder over the engine's honored grammar. Fully
 * controlled: every interaction maps builder state through `emitRRule`'s
 * closed emission table — raw rule text is never typed, only displayed
 * read-only in the Advanced disclosure. The Ends radio makes COUNT-vs-end-date
 * mutual exclusivity structural, and "Ends on" writes `endDate` (never UNTIL).
 */
export function RecurrenceBuilder({ value, onChange }: RecurrenceBuilderProps) {
  const { t, i18n } = useTranslation(["test-cadence"]);
  const locale = i18n.language;
  const { state, startDate, endDate, horizonDays } = value;
  const hasStartDate = startDate !== "";

  const matchedPreset = matchPreset(state, startDate);
  // "Custom…" stays revealed once chosen, even when edits land back on a
  // preset-shaped state; states no preset expresses force the panel open.
  const [customSelected, setCustomSelected] = useState(
    () => value.startDate !== "" && matchPreset(value.state, value.startDate) === null,
  );
  const isCustom = customSelected || (hasStartDate && matchedPreset === null);

  const endsGroup = useId();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const rrule = emitRRule(state);
  const preview = useOccurrencePreview({ rrule, startDate, endDate });
  const summary = hasStartDate ? summarizeRecurrence(t, locale, state, startDate, endDate) : null;

  const presetOptions = hasStartDate
    ? [
        { value: "daily", label: t(`${RECURRENCE}.daily`) },
        {
          value: "weekly",
          label: t(`${RECURRENCE}.weeklyOn`, {
            weekday: new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
              parseLocalDate(startDate),
            ),
          }),
        },
        { value: "everyWeekday", label: t(`${RECURRENCE}.everyWeekday`) },
        {
          value: "monthly",
          label: t(`${RECURRENCE}.monthlyOnDay`, { day: parseLocalDate(startDate).getDate() }),
        },
        {
          value: "quarterly",
          label: t(`${RECURRENCE}.quarterlyOnDay`, { day: parseLocalDate(startDate).getDate() }),
        },
        {
          value: "yearly",
          label: t(`${RECURRENCE}.annuallyOn`, {
            date: new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(
              parseLocalDate(startDate),
            ),
          }),
        },
        { value: "custom", label: t(`${RECURRENCE}.custom`) },
      ]
    : [{ value: "custom", label: t(`${RECURRENCE}.custom`) }];
  const selectValue =
    hasStartDate && !isCustom && matchedPreset !== null ? matchedPreset : "custom";

  const unitOptions = [
    { value: "daily", label: t(`${RECURRENCE}.unitDay`, { count: state.interval }) },
    { value: "weekly", label: t(`${RECURRENCE}.unitWeek`, { count: state.interval }) },
    { value: "monthly", label: t(`${RECURRENCE}.unitMonth`, { count: state.interval }) },
    { value: "yearly", label: t(`${RECURRENCE}.unitYear`, { count: state.interval }) },
  ];

  function updateState(patch: Partial<RecurrenceBuilderState>) {
    onChange({ ...value, state: { ...state, ...patch } });
  }

  function handleStartDateChange(nextDate: string) {
    if (!isCustom && matchedPreset !== null && nextDate !== "") {
      // Preset states are start-date-anchored — re-derive so "Weekly on
      // Wednesday" stays truthful when the anchor moves to another weekday.
      onChange({ ...value, startDate: nextDate, state: presetState(matchedPreset, nextDate) });
      return;
    }
    onChange({ ...value, startDate: nextDate });
  }

  function handlePresetChange(selected: string) {
    if (selected === "custom") {
      setCustomSelected(true);
      return;
    }
    setCustomSelected(false);
    // The select's option values are exactly the RecurrencePreset ids plus
    // "custom", so the narrowing is sound after the "custom" branch returns.
    onChange({
      ...value,
      state: presetState(selected as RecurrencePreset, startDate),
      endDate: null,
    });
  }

  function handleFrequencyChange(selected: string) {
    // The unit select's option values are exactly the RecurrenceFrequency ids.
    const freq = selected as RecurrenceFrequency;
    const defaults = defaultStateFor(freq, startDate);
    // Frequency switch preserves interval + ends; the chip axes are
    // frequency-scoped and reset to their start-date-derived defaults.
    onChange({ ...value, state: { ...defaults, interval: state.interval, ends: state.ends } });
  }

  function toggleWeekday(day: Weekday) {
    const next = new Set(state.weekdays);
    if (next.has(day)) {
      if (next.size === 1) return; // selection floor of 1 — structural no-op
      next.delete(day);
    } else {
      next.add(day);
    }
    updateState({ weekdays: next });
  }

  function toggleMonthDay(day: number) {
    const next = new Set(state.monthDays);
    if (next.has(day)) {
      if (next.size === 1) return; // selection floor of 1 — structural no-op
      next.delete(day);
    } else {
      next.add(day);
    }
    updateState({ monthDays: next });
  }

  function setEndsNever() {
    onChange({ ...value, state: { ...state, ends: { type: "never" } }, endDate: null });
  }

  function setEndsOn(date: string) {
    onChange({
      ...value,
      state: { ...state, ends: { type: "on", date } },
      endDate: date === "" ? null : date,
    });
  }

  function setEndsAfter(count: number) {
    onChange({ ...value, state: { ...state, ends: { type: "after", count } }, endDate: null });
  }

  function handleCopy() {
    void navigator.clipboard?.writeText(rrule).then(() => {
      setCopied(true);
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="w-56">
          <FormField
            label={t(`${RECURRENCE}.startDate`)}
            required
            hint={t(`${RECURRENCE}.startDateHint`)}
          >
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className={inputClass}
            />
          </FormField>
        </div>
        <div className="min-w-70 flex-1">
          <FormField
            label={t(`${RECURRENCE}.repeats`)}
            required
            hint={t(`${RECURRENCE}.repeatsHint`)}
          >
            <Select
              options={presetOptions}
              value={selectValue}
              disabled={!hasStartDate}
              onChange={(e) => handlePresetChange(e.target.value)}
            />
          </FormField>
        </div>
      </div>

      {isCustom && hasStartDate && (
        <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-sm font-medium text-on-surface">
              {t(`${RECURRENCE}.repeatEvery`)}
            </span>
            <Stepper
              value={state.interval}
              min={1}
              decreaseLabel={t(`${RECURRENCE}.decrease`)}
              increaseLabel={t(`${RECURRENCE}.increase`)}
              onChange={(interval) => updateState({ interval })}
            />
            <div className="w-36">
              <Select
                aria-label={t(`${RECURRENCE}.repeatEvery`)}
                options={unitOptions}
                value={state.freq}
                onChange={(e) => handleFrequencyChange(e.target.value)}
              />
            </div>
          </div>

          {state.freq === "weekly" && (
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_ORDER.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  aria-pressed={state.weekdays.has(day)}
                  onClick={() => toggleWeekday(day)}
                  className={`${chipClass(state.weekdays.has(day))} w-12`}
                >
                  {/* Monday 2024-01-01 anchors localized chip names for MO..SU. */}
                  {new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
                    new Date(2024, 0, 1 + index),
                  )}
                </button>
              ))}
            </div>
          )}

          {state.freq === "monthly" && (
            <div>
              <p className="mb-2 text-sm font-semibold text-on-surface">
                {t(`${RECURRENCE}.onDaysOfMonth`)}
              </p>
              <div className="grid w-fit grid-cols-7 gap-1.5">
                {MONTH_DAYS.map((day) => (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={state.monthDays.has(day)}
                    onClick={() => toggleMonthDay(day)}
                    className={`${chipClass(state.monthDays.has(day))} w-10`}
                  >
                    {day}
                  </button>
                ))}
                <button
                  type="button"
                  aria-pressed={state.monthDays.has(-1)}
                  onClick={() => toggleMonthDay(-1)}
                  className={`${chipClass(state.monthDays.has(-1))} col-span-3 px-3`}
                >
                  {t(`${RECURRENCE}.lastDayOfMonth`)}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted">{t(`${RECURRENCE}.shortMonthHint`)}</p>
            </div>
          )}

          {state.freq === "yearly" && (
            <p className="text-sm text-muted">
              {t(`${RECURRENCE}.yearlyAnchorNote`, {
                date: new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(
                  parseLocalDate(startDate),
                ),
              })}
            </p>
          )}

          <div>
            <p className="mb-1 text-sm font-semibold text-on-surface">{t(`${RECURRENCE}.ends`)}</p>
            <div className="flex items-center gap-2.5 py-1.5">
              <input
                type="radio"
                id={`${endsGroup}-never`}
                name={endsGroup}
                checked={state.ends.type === "never"}
                onChange={setEndsNever}
              />
              <label
                htmlFor={`${endsGroup}-never`}
                className="w-20 flex-none text-sm text-on-surface"
              >
                {t(`${RECURRENCE}.endsNever`)}
              </label>
              <span className="text-xs text-muted">{t(`${RECURRENCE}.endsNeverHint`)}</span>
            </div>
            <div className="flex items-center gap-2.5 py-1.5">
              <input
                type="radio"
                id={`${endsGroup}-on`}
                name={endsGroup}
                checked={state.ends.type === "on"}
                onChange={() => setEndsOn(endDate ?? "")}
              />
              <label htmlFor={`${endsGroup}-on`} className="w-20 flex-none text-sm text-on-surface">
                {t(`${RECURRENCE}.endsOn`)}
              </label>
              <div className="w-44">
                <input
                  type="date"
                  aria-label={t(`${RECURRENCE}.endsOnDate`)}
                  disabled={state.ends.type !== "on"}
                  value={state.ends.type === "on" ? state.ends.date : ""}
                  onChange={(e) => setEndsOn(e.target.value)}
                  className={`${inputClass} disabled:opacity-50`}
                />
              </div>
            </div>
            <div className="flex items-center gap-2.5 py-1.5">
              <input
                type="radio"
                id={`${endsGroup}-after`}
                name={endsGroup}
                checked={state.ends.type === "after"}
                onChange={() => setEndsAfter(DEFAULT_AFTER_COUNT)}
              />
              <label
                htmlFor={`${endsGroup}-after`}
                className="w-20 flex-none text-sm text-on-surface"
              >
                {t(`${RECURRENCE}.endsAfter`)}
              </label>
              <Stepper
                value={state.ends.type === "after" ? state.ends.count : DEFAULT_AFTER_COUNT}
                min={1}
                max={MAX_AFTER_COUNT}
                disabled={state.ends.type !== "after"}
                decreaseLabel={t(`${RECURRENCE}.decrease`)}
                increaseLabel={t(`${RECURRENCE}.increase`)}
                onChange={setEndsAfter}
              />
              <span className="text-sm text-muted">
                {t(`${RECURRENCE}.occurrences`)}{" "}
                <span className="text-xs">({t(`${RECURRENCE}.occurrencesMax`)})</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {summary && (
        <div className="flex items-start gap-2.5 rounded-lg border border-primary/25 bg-primary/10 px-4 py-3">
          <CalendarIcon className="mt-0.5 h-5 w-5 flex-none text-primary" />
          <div>
            <p className="text-sm font-medium text-on-surface">{summary.sentence}</p>
            {summary.cadenceLine && (
              <p className="mt-0.5 text-xs text-muted">{summary.cadenceLine}</p>
            )}
          </div>
        </div>
      )}

      <OccurrencePreview preview={preview} startDate={startDate} horizonDays={horizonDays} />

      <details className="rounded-lg border border-gray-200 dark:border-gray-700">
        <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-semibold text-muted">
          {t(`${ADVANCED}.title`)}
        </summary>
        <div className="border-t border-gray-100 px-4 pb-4 dark:border-gray-700">
          <p className="mt-3 text-sm font-medium text-on-surface">
            {t(`${ADVANCED}.generatedRule`)}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 rounded-md border border-gray-200 bg-gray-100 px-3 py-2 font-mono text-sm text-on-surface dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              {rrule}
            </code>
            <Button type="button" variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? t(`${ADVANCED}.copied`) : t(`${ADVANCED}.copy`)}
            </Button>
          </div>
          <p className="mt-1 text-xs text-muted">{t(`${ADVANCED}.generatedRuleHint`)}</p>
          <div className="mt-4 max-w-60">
            <FormField label={t(`${ADVANCED}.horizonDays`)} hint={t(`${ADVANCED}.horizonHint`)}>
              <input
                type="number"
                min={1}
                step={1}
                value={horizonDays}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isInteger(next) && next >= 1) {
                    onChange({ ...value, horizonDays: next });
                  }
                }}
                className={inputClass}
              />
            </FormField>
          </div>
        </div>
      </details>
    </div>
  );
}
